import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/Header';
import { api } from '../../api/client';
import {
  UserCircle,
  Phone,
  Mail,
  Shield,
  Save,
  Lock,
  LogOut,
  AlertTriangle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';

export default function FieldProfileScreen() {
  const goBack = useBack('/field');
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  // Keys must match the PATCH /employee/my-profile allowlist (camelCase) exactly.
  const [form, setForm] = useState({ phoneNumber: '', emergencyContactName: '', emergencyContactPhone: '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: profile, isLoading } = useQuery<any>({
    queryKey: ['field-profile'],
    queryFn: () => api.employee.myProfile().then(r => r.data.data ?? r.data),
  });

  useEffect(() => {
    if (profile) {
      setForm({
        phoneNumber: profile.phone_number ?? '',
        emergencyContactName: profile.emergency_contact_name ?? '',
        emergencyContactPhone: profile.emergency_contact_phone ?? '',
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.employee.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-profile'] });
      setEditing(false);
      setMessage({ type: 'success', text: 'Profile updated' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: () => setMessage({ type: 'error', text: 'Failed to update profile' }),
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) => api.employee.changePassword(data),
    onSuccess: () => {
      setChangingPassword(false);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Password changed' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err: any) => setMessage({ type: 'error', text: err.response?.data?.error ?? 'Failed to change password' }),
  });

  const handleSaveProfile = () => {
    updateMutation.mutate(form);
  };

  const handleChangePassword = () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    passwordMutation.mutate({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto"><Header /></div>
        <div className="flex items-center justify-center py-20 text-fieldiq-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Profile</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {message && (
          <div className={`rounded-xl p-3 flex items-center gap-2 ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-fieldiq-accent/20 flex items-center justify-center">
              <UserCircle size={40} className="text-fieldiq-accent" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{profile?.first_name} {profile?.last_name}</h2>
              <p className="text-sm text-fieldiq-text capitalize">{profile?.role?.replace('_', ' ')}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Mail size={16} className="text-fieldiq-muted" />
              <span className="text-fieldiq-text">{profile?.email}</span>
            </div>
            {profile?.hire_date && (
              <div className="flex items-center gap-3 text-sm">
                <Shield size={16} className="text-fieldiq-muted" />
                <span className="text-fieldiq-text">Since {profile.hire_date}</span>
              </div>
            )}
          </div>
        </div>

        {/* Editable Fields */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Contact Info</h3>
            {!editing && (
              <button onClick={() => setEditing(true)} className="text-sm text-fieldiq-accent">Edit</button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={form.phoneNumber}
                  onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={16} className="text-fieldiq-muted" />
                  <span>{profile?.phone_number || 'Not set'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Emergency Contact Name</label>
              {editing ? (
                <input
                  type="text"
                  value={form.emergencyContactName}
                  onChange={e => setForm(f => ({ ...f, emergencyContactName: e.target.value }))}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
              ) : (
                <p className="text-sm">{profile?.emergency_contact_name || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Emergency Contact Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={form.emergencyContactPhone}
                  onChange={e => setForm(f => ({ ...f, emergencyContactPhone: e.target.value }))}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
              ) : (
                <p className="text-sm">{profile?.emergency_contact_phone || 'Not set'}</p>
              )}
            </div>

            {editing && (
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Save size={16} /> Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 bg-fieldiq-dark py-2 rounded-lg text-fieldiq-text"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          {!changingPassword ? (
            <button
              onClick={() => setChangingPassword(true)}
              className="w-full flex items-center gap-3 text-left"
            >
              <Lock size={20} className="text-fieldiq-text" />
              <span className="font-semibold">Change Password</span>
            </button>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold">Change Password</h3>
              <input
                type="password"
                placeholder="Current password"
                value={pwForm.currentPassword}
                onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
              />
              <input
                type="password"
                placeholder="New password"
                value={pwForm.newPassword}
                onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={pwForm.confirmPassword}
                onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleChangePassword}
                  disabled={passwordMutation.isPending}
                  className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold py-2 rounded-lg"
                >
                  {passwordMutation.isPending ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  onClick={() => setChangingPassword(false)}
                  className="flex-1 bg-fieldiq-dark py-2 rounded-lg text-fieldiq-text"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full bg-red-500/20 text-red-400 rounded-xl p-4 flex items-center justify-center gap-2 font-semibold"
        >
          <LogOut size={20} /> Sign Out
        </button>
      </div>
    </div>
  );
}
